<template>
  <DetailSplitView :ready="violationReady" :show-panel="Boolean(panel)">
    <template #top>
      <slot name="navigation" />

      <template v-if="violationReady">
        <template v-if="violation.hasDeferral">
          <StatusBadge v-if="violation.isWhitelisted" variant="warning">
            Deferred{{ violation.deferralEndDate ? ` until ${violation.deferralEndDate.format('MMM D, YYYY')}` : '' }}
          </StatusBadge>
          <StatusBadge v-if="violation.hasEndedDeferral" variant="secondary">
            Deferral ended{{ violation.deferralEndDate ? ` ${violation.deferralEndDate.format('MMM D, YYYY')}` : '' }}
          </StatusBadge>
        </template>

        <div class="ms-auto d-flex flex-row align-items-center">

          <IconButton
            :icon="violation.correspondences.length > 0 ? 'envelope-fill' : 'envelope'"
            :title="(violation.correspondences.length || 'No') + ' Letter' + (violation.correspondences.length === 1 ? '' : 's')"
            :class="['flex-shrink-0 panel-tab', { 'panel-tab--active': panel === 'correspondence'}]"
            @click="togglePanel('correspondence')"
          />
          <IconButton
            icon="chat"
            :title="(violation.commentCount || 'No') + ' Comment' + (violation.commentCount === 1 ? '' : 's')"
            :class="['flex-shrink-0 panel-tab', { 'panel-tab--active': panel === 'discussion' }]"
            @click="togglePanel('discussion')"
          />
          <IconButton
            :icon="violation.isWhitelisted ? 'clock-fill' : violation.hasEndedDeferral ? 'clock-history' : 'clock'"
            :title="violation.isWhitelisted ? 'Deferred / Whitelisted' : violation.hasEndedDeferral ? 'Previously deferred' : 'Defer / Whitelist'"
            :class="['flex-shrink-0 panel-tab', { 'panel-tab--active': panel === 'whitelist' }]"
            @click="togglePanel('whitelist')"
          />

          <div class="dropdown">
            <IconButton
              button
              icon="three-dots"
              class="flex-shrink-0 dropdown-toggle no-caret"
              data-bs-toggle="dropdown"
            />
            <ul class="dropdown-menu">
              <li>
                <IconButton
                  text="Create a license"
                  icon="file-earmark-plus"
                  @click="createLicense"
                  small align="start" class="dropdown-item"
                />
              </li>
              <li>
                <IconButton
                  icon="geo-alt"
                  text="Re-center map"
                  @click="showLocation"
                  small align="start" class="dropdown-item"
                />
              </li>
              <li>
                <IconButton
                  v-if="violation.isViolationSeen"
                  icon="dot"
                  text="Mark as new"
                  @click="toggleSeen"
                  small align="start" class="dropdown-item"
                />
              </li>
            </ul>
          </div>
        </div>
      </template>
    </template>

    <template #left>
      <div class="violation-content d-flex flex-column flex-grow-1 overflow-auto" @scroll="updateScroll" ref="scroller">

        <!-- Image -->
        <expandable-image v-if="violation.imageUrl" :src="violation.imageUrl" />

        <!-- Property -->
        <PropertySection :property="violation" />

        <!-- Ownwer -->
        <detail-section title="Owner">
          <div class="section-content">
            <!-- Name -->
            <div v-for="owner in (violation?.owners || [])" :key="owner">
              {{ formatName(owner, 'all', 1) }}
            </div>
            <!-- Mailing address -->
            <div v-for="(line, i) in violation?.ownerAddressLines" :key="`address_line_${i}`">
              {{ line }}
            </div>
            <div v-if="(violation?.ownerAddressLines || []).length === 0" class="text-muted small">(No mailing address)</div>
          </div>
        </detail-section>

        <detail-section title="Compliance Status">
          <StatusBadge :variant="statusBadge.variant" class="align-self-start">
            {{ statusBadge.label }}
          </StatusBadge>
        </detail-section>

        <detail-section :title="formatNumber(filteredListings.length, 'Listing')" flush>
          <template #buttons>
            <div v-if="inactiveListingCount > 0" class="form-check form-switch mb-0 small me-2">
              <input class="form-check-input" type="checkbox" id="showInactiveToggle" v-model="showInactive" />
              <label class="form-check-label text-muted" for="showInactiveToggle">
                Include inactive ({{ inactiveListingCount }})
              </label>
            </div>
          </template>
          <div v-if="filteredListings.length > 0" class="list-group list-group-flush license-listings">
            <ListingNavigationRow
              v-for="listing in filteredListings"
              :key="listing.id"
              :listing="listing"
              class="px-3"
              hide-address
            />
          </div>
          <div v-else class="text-center text-muted py-5 px-3">
            No listings found
          </div>
        </detail-section>
      </div>
    </template>

    <template #right>
      <div v-if="panel === 'correspondence'" class="w-100">
        <CorrespondencesView :property="violation" class="p-3" />
      </div>
      <div v-else-if="panel === 'whitelist'" class="w-100">
        <WhitelistEditor :model="violation" noun="property" class="p-3" />
      </div>
      <div v-else-if="panel === 'discussion'" class="w-100 h-100">
        <ActivityPanel :model="violation" class="h-100"/>
      </div>
    </template>

    <template #fallback>
      <div class="d-flex flex-column align-items-center justify-content-center flex-grow-1 p-4">
        <LoadingMessage v-if="loadingViolation" message="Loading violation" />
        <div v-else-if="violationLoadError" class="text-danger text-center small">
          Could not load violation details.
        </div>
        <div v-else class="text-muted text-center small">
          Violation not found.
        </div>
      </div>
    </template>
  </DetailSplitView>

  <EditLicenseModal v-model:license="editLicense" />
</template>

<script>
import { applyLegacyViolationSummary } from '@/violationDetail';
import IconButton from "@/components/common/IconButton.vue";
import WhitelistEditor from "@/components/common/WhitelistEditor.vue";
import EditLicenseModal from "@/components/modals/EditLicenseModal.vue";
import CorrespondencesView from "@/components/common/CorrespondencesView.vue";
import DetailSplitView from "@/components/common/DetailSplitView.vue";
import {License} from "@/models/License";
import ActivityPanel from "@/components/common/ActivityPanel.vue";
import {Property} from "@/models/Property";
import {Listing} from "@/models/Listing";
import StatusBadge from "@/components/common/StatusBadge.vue";
import LoadingMessage from "@/components/common/LoadingMessage.vue";
import ListingNavigationRow from "@/components/listing/ListingNavigationRow.vue";
import PropertySection from "@/components/properties/PropertySection.vue";
import DetailSection from "@/components/common/DetailSection.vue";
import ExpandableImage from "@/components/common/ExpandableImage.vue";

export default {
  components: {
    ExpandableImage,
    DetailSection,
    PropertySection,
    DetailSplitView,
    LoadingMessage,
    StatusBadge,
    ListingNavigationRow,
    ActivityPanel,
    CorrespondencesView,
    EditLicenseModal,
    WhitelistEditor,
    IconButton
  },
  emits: ['close','showLocation','openStreetView', 'update:panel'],
  props: {
    city: Object,
    violationId: String,
    violationSummary: Object,
    panel: String
  },
  data() {
    return {
      violation: null,  // @type {Property}
      loadingViolation: false,
      violationLoadError: null,
      loadToken: 0,
      listings: [],
      loadingListings: false,
      showInactive: false,
      loading: false,
      scrolled: false,
      printing: false,
      editLicense: null
    };
  },
  watch: {
    loadKey: {
      immediate: true,
      handler() {
        this.loadViolation();
      }
    },
    violationSummary() { this.applyViolationSummary(); }
  },
  methods: {
    applyViolationSummary() {
      applyLegacyViolationSummary(this.violation, this.violationSummary, this.city);
    },
    async loadViolation() {
      const token = ++this.loadToken;
      this.loadingViolation = true;
      this.violationLoadError = null;
      this.listings = [];
      const previousViolationId = this.violation?.id || null;

      if (!this.city || !this.violationId) {
        this.violation = null;
        this.loadingViolation = false;
        return;
      }
      this.violation = null;

      try {
        const violation = await Property.one(this.city, this.violationId);
        if (token !== this.loadToken) {
          return;
        }
        const isDifferent = violation?.id !== previousViolationId;
        this.violation = violation;
        this.applyViolationSummary();
        if (isDifferent && !this.panel) {
          this.$emit('update:panel', 'correspondence');
        }
        await this.reload(isDifferent);
      } catch (error) {
        if (token !== this.loadToken) {
          return;
        }
        console.error(`Could not load violation ${this.violationId}`, error);
        this.violation = null;
        this.violationLoadError = error;
      } finally {
        if (token === this.loadToken) {
          this.loadingViolation = false;
        }
      }
    },
    createLicense() {
      if (!this.violation) {
        return;
      }
      const profileLabel = `[ViolationDetailView#createLicense ${Date.now()}] click -> modal state updated`;
      console.time(profileLabel);
      this.editLicense = License.createDraftForProperty(this.violation, {
        listings: this.listings.map(listing => listing.url)
      });
      this.$nextTick(() => {
        console.timeEnd(profileLabel);
      });
    },
    togglePanel(panelName) {
      this.$emit('update:panel', this.panel === panelName ? null : panelName);
    },
    async reload(isDifferent) {
      if (!this.violation) {
        return;
      }
      if (isDifferent) {
        this.showLocation();
      }
      if (this.$refs.scroller) {
        this.$refs.scroller.scrollTo(0, 0);
      }
      if (isDifferent) {
        await this.violation.updateViolationSeen(true);
        this.loadingListings = true;
        try {
          const response = await Listing.query(this.violation.city, {
            constraints: [['verifiedPropertyId', '==', this.violation.id]]
          });
          this.listings = response.results;
        } finally {
          this.loadingListings = false;
        }
      }
    },
    showLocation() {
      if (this.violation?.latlng) {
        this.$emit('showLocation', this.violation.latlng);
      }
    },
    close(message) {
      this.$emit('close', message || false);
    },
    updateScroll(e) {
      this.scrolled = e.target.scrollTop >= 50;
    },
    toggleSeen() {
      if (this.violation) {
        this.violation.updateViolationSeen(!this.violation.isViolationSeen);
      }
    }
  },
  computed: {
    loadKey() {
      return `${this.city?.id || ''}/${this.violationId || ''}`;
    },
    violationReady() {
      return Boolean(this.violation?.hasData);
    },
    filteredListings() {
      if (this.showInactive) return this.listings;
      return this.listings.filter(l => !l.isInactive);
    },
    inactiveListingCount() {
      return this.listings.filter(l => l.isInactive).length;
    },
    statusBadge() {
      if (this.violationReason) {
        return {
          label: this.violationReason,
          variant: 'danger'
        };
      } else {
        return {
          label: 'Compliant',
          variant: 'success'
        };
      }
    },
    violationReason() {
      // TODO maybe store this in the violations object?
      const violations = this.violation?.violations;
      if (violations?.status === 'violation') {
        if (violations.licenseStatus === 'none') {
          return 'Not licensed';
        } else if (violations.licenseStatus === 'expired') {
          return 'License expired';
        } else {
          return `License status: ${violations.licenseWorkflowStatus || 'unknown'};`
        }
      } else {
        return null;
      }
    }
  }
}
</script>

<style scoped>
</style>
